import pluralize from "pluralize";
import globalStore from "../../../../../state";
import type { WorkspaceState } from "../../../../../state/slices";
import { ActivityStatus, ActivityUIKind, AsyncTaskName, AsyncTaskStatus } from "../constants";
import type { ActivityViewData, AsyncTaskMeta, AsyncTaskTypeMapping, ShowModalFn } from "../types";
import { parseTaskParams, parseTaskParamsArray } from "@rflocal/shared/asyncTasks/parseTaskParams";
import { toDate } from "./utils";
import type { FirestoreTimestampLike, FirestoreAsyncTask } from "./types";
import { getExportDatasetAction } from "../../ImageBrowser/ExportDatasetModal/exportDatasetSnippetBridge";

// Shared helpers — used by multiple mapping factories

function getDataset(owner: string, datasetId: string) {
    try {
        const state = globalStore.getState() as Pick<WorkspaceState, "workspaceDatasetsById">;
        return state.workspaceDatasetsById?.[owner]?.[datasetId] || null;
    } catch {
        return null;
    }
}

function getDatasetAppUrl(owner: string, datasetId: string): string | null {
    try {
        const workspaceUrl = getWorkspaceUrl(owner);
        if (!workspaceUrl) return null;
        const dataset = getDataset(owner, datasetId);
        if (!dataset?.url) return null;
        return `/${workspaceUrl}/${dataset.url}`;
    } catch {
        return null;
    }
}

function getWorkspaceUrl(workspaceId: string): string | null {
    const state = globalStore.getState() as Pick<WorkspaceState, "workspacesById">;
    return state.workspacesById?.[workspaceId]?.url ?? null;
}

function getCurrentWorkspace(): string | undefined {
    const state = globalStore.getState() as Pick<WorkspaceState, "currentWorkspaceId">;
    return state.currentWorkspaceId ?? undefined;
}

// Mapping factories — one function per async task type.
// Private helpers belong INSIDE the factory that uses them.

function createRemapClasses(): AsyncTaskTypeMapping {
    return {
        icon: "far fa-exchange-alt",
        title: "Remap classes",
        openInNewTab: true,
        toastOnDispatch: false,
        toastOnSuccess: false,
        toastOnFailure: false,
        getDescription: (task) => {
            const args =
                parseTaskParamsArray<[unknown, string, string[], Record<string, string>]>(task);
            if (!args) return null;
            const [, projectId, classesToDelete, remap] = args;
            const dataset = getDataset(task.owner, projectId);
            const datasetName = dataset?.url || projectId;
            const parts: string[] = [];
            const remapKeys = Object.keys(remap || {});
            if (remapKeys.length)
                parts.push(`${remapKeys.length} renamed ${pluralize("class", remapKeys.length)}`);
            if (classesToDelete?.length)
                parts.push(
                    `${classesToDelete.length} deleted ${pluralize("class", classesToDelete.length)}`
                );
            if (!parts.length) return null;
            return `${parts.join(", ")} on dataset ${datasetName}`;
        },
        getTargetUrl: (task) => {
            const args = parseTaskParamsArray<[unknown, string]>(task);
            if (!args) return null;
            const [, projectId] = args;
            return getDatasetAppUrl(task.owner, projectId);
        }
    };
}

function createMergeDatasets(): AsyncTaskTypeMapping {
    type MergeParams = { datasetIds?: string[]; targetDataset?: { name?: string; url?: string } };
    return {
        icon: "far fa-object-group",
        title: "Merge datasets",
        openInNewTab: true,
        toastOnDispatch: false,
        toastOnSuccess: false,
        toastOnFailure: false,
        getDescription: (task) => {
            const params = parseTaskParams<MergeParams>(task);
            const count = params?.datasetIds?.length || 0;
            const targetName = params?.targetDataset?.name;
            if (!targetName) return null;
            return `${count} ${pluralize("dataset", count)} into ${targetName}`;
        },
        getTargetUrl: (task) => {
            const params = parseTaskParams<MergeParams>(task);
            const targetUrl = params?.targetDataset?.url;
            if (!targetUrl) return null;
            const workspaceUrl = getWorkspaceUrl(task.owner);
            return workspaceUrl ? `/${workspaceUrl}/${targetUrl}` : null;
        }
    };
}

function createCopyProjectImages(): AsyncTaskTypeMapping {
    type CopyParams = {
        sourceDatasetId?: string;
        destinationDatasetId?: string;
        keepAnnotations?: boolean;
    };
    return {
        icon: "far fa-copy",
        title: "Copy project images",
        openInNewTab: true,
        toastOnDispatch: false,
        toastOnSuccess: false,
        toastOnFailure: false,
        getDescription: (task) => {
            const params = parseTaskParams<CopyParams>(task);
            if (!params) return null;
            const { sourceDatasetId, destinationDatasetId, keepAnnotations } = params;
            const workspace = getCurrentWorkspace();
            if (!workspace) return null;
            const source = sourceDatasetId ? getDataset(workspace, sourceDatasetId) : null;
            const destination = destinationDatasetId
                ? getDataset(workspace, destinationDatasetId)
                : null;
            if (!source || !destination) return null;
            return `From ${source.name} to ${destination.name}${keepAnnotations ? " with annotations" : ""}`;
        },
        getTargetUrl: (task) => {
            try {
                const result = JSON.parse(task.result || "");
                return getDatasetAppUrl(task.owner, result?.destinationProjectId);
            } catch {
                return null;
            }
        }
    };
}

function createExportDataset(): AsyncTaskTypeMapping {
    type ExportParams = {
        format?: string;
        name?: string;
        link?: string;
        datasetId?: string | null;
        exportType?: string;
        datasetName?: string | null;
        version?: string | number;
        imageCount?: number | null;
        isFiltered?: boolean;
    };

    type Source = "projectSearch" | "versionDownload" | "assetLibrary";

    function getSource(params: ExportParams | null): Source {
        if (params?.exportType === "version") return "versionDownload";
        if (params?.datasetId) return "projectSearch";
        return "assetLibrary";
    }

    function formatCount(count: number): string {
        if (count >= 1_000_000) return `${Math.floor((count / 1_000_000) * 10) / 10}M images`;
        if (count >= 1_000) return `${Math.floor((count / 1_000) * 10) / 10}k images`;
        return `${count} ${pluralize("image", count)}`;
    }

    return {
        icon: "far fa-file-zipper",
        title: (task) => {
            const source = getSource(parseTaskParams<ExportParams>(task));
            const label =
                source === "assetLibrary"
                    ? "Images export"
                    : source === "versionDownload"
                      ? "Dataset version export"
                      : "Dataset export";
            if (task.status === AsyncTaskStatus.Completed) return `${label} ready`;
            if (task.status === AsyncTaskStatus.Failed) return `${label} failed`;
            return label;
        },
        statusLabel: "Zipping",
        openInNewTab: false,
        completionNote: "Download link expires after 7 days",
        getContextLabel: (task) => {
            const params = parseTaskParams<ExportParams>(task);
            const source = getSource(params);
            if (source === "assetLibrary") return "Asset Library";
            const name =
                params?.datasetName || (source !== "versionDownload" ? params?.name : null) || null;
            if (source === "versionDownload") {
                if (!name) return null;
                return params?.version ? `${name} \u00b7 v${params.version}` : name;
            }
            return name;
        },
        getDescription: (task) => {
            const params = parseTaskParams<ExportParams>(task);
            const parts: string[] = [];
            if (params?.format) parts.push(params.format.toUpperCase());
            if (params?.imageCount) parts.push(formatCount(params.imageCount));
            if (params?.isFiltered) parts.push("Filtered");
            return parts.join(" \u00b7 ") || null;
        },
        getTargetUrl: (task) => {
            if (task.status !== AsyncTaskStatus.Completed || !task.result) return null;
            try {
                return JSON.parse(task.result).downloadUrl || null;
            } catch {
                return null;
            }
        },
        getAction: (task, showModal) => {
            if (task.status !== AsyncTaskStatus.Completed || !task.result) return null;
            try {
                const result = JSON.parse(task.result);
                if (!result?.downloadUrl) return null;
                return getExportDatasetAction(
                    task.id,
                    result.downloadUrl,
                    parseTaskParams<ExportParams>(task),
                    showModal
                );
            } catch {
                return null;
            }
        },
        getMeta: (task) => {
            const params = parseTaskParams<ExportParams>(task);
            return params?.link ? { exportLink: params.link } : undefined;
        },
        getErrorMessage: () => "Something went wrong preparing your export. Please try again."
    };
}

// Registry — invoke factories, register by taskName

const ASYNC_TASK_TYPES: Record<AsyncTaskName, AsyncTaskTypeMapping> = {
    [AsyncTaskName.RemapClasses]: createRemapClasses(),
    [AsyncTaskName.MergeDatasets]: createMergeDatasets(),
    [AsyncTaskName.CopyProjectImages]: createCopyProjectImages(),
    [AsyncTaskName.ExportDataset]: createExportDataset()
};

// Converter — generic, delegates to registry

function toStatusEnum(firestoreStatus: string): ActivityStatus {
    if (firestoreStatus === AsyncTaskStatus.Created) return ActivityStatus.Pending;
    if (firestoreStatus === AsyncTaskStatus.Running) return ActivityStatus.Running;
    if (firestoreStatus === AsyncTaskStatus.Completed) return ActivityStatus.Completed;
    if (firestoreStatus === AsyncTaskStatus.Failed) return ActivityStatus.Failed;
    return ActivityStatus.Pending;
}

function extractError(task: FirestoreAsyncTask): string | null {
    if (task.status !== AsyncTaskStatus.Failed || !task.result) return null;
    try {
        const parsed = JSON.parse(task.result);
        return parsed?.message || "Unknown error";
    } catch {
        return "Unknown error";
    }
}

function toDateOrNull(ts: FirestoreTimestampLike | undefined | null): Date | null {
    return ts ? toDate(ts) : null;
}

function resolvePhaseGate(
    gate: boolean | ((task: FirestoreAsyncTask) => boolean) | undefined,
    task: FirestoreAsyncTask
): boolean {
    if (gate === undefined) return true;
    if (typeof gate === "boolean") return gate;
    return gate(task);
}

/** Returns null for unregistered task types — they're silently filtered from the activity feed. */
export function convertAsyncTask(
    task: FirestoreAsyncTask,
    showModal: ShowModalFn
): ActivityViewData | null {
    const mapping = ASYNC_TASK_TYPES[task.taskName as AsyncTaskName];
    if (!mapping) return null;

    try {
        const action = mapping.getAction?.(task, showModal) ?? null;
        const extraMeta = mapping.getMeta?.(task);

        const meta: AsyncTaskMeta = {
            taskName: task.taskName,
            ...(mapping.completionNote ? { completionNote: mapping.completionNote } : {}),
            ...(action ? { action } : {}),
            toast: {
                strategy: mapping.toastStrategy ?? "persistent",
                dispatch: resolvePhaseGate(mapping.toastOnDispatch, task),
                success: resolvePhaseGate(mapping.toastOnSuccess, task),
                failure: resolvePhaseGate(mapping.toastOnFailure, task)
            },
            ...extraMeta
        };

        return {
            id: task.id,
            kind: ActivityUIKind.AsyncTask,
            title: typeof mapping.title === "function" ? mapping.title(task) : mapping.title,
            contextLabel: mapping.getContextLabel?.(task) ?? undefined,
            description: mapping.getDescription?.(task) ?? null,
            status: toStatusEnum(task.status),
            ...(mapping.statusLabel ? { statusLabel: mapping.statusLabel } : {}),
            icon: mapping.icon,
            targetUrl: mapping.getTargetUrl?.(task) ?? null,
            openInNewTab: mapping.openInNewTab,
            progress: task.progress?.total
                ? { current: task.progress.current || 0, total: task.progress.total }
                : null,
            error:
                task.status === AsyncTaskStatus.Failed
                    ? (mapping.getErrorMessage?.(task) ?? extractError(task))
                    : null,
            createdAt: toDate(task.createdAt),
            completedAt: toDateOrNull(task.completedAt || task.failedAt),
            meta
        };
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`convertAsyncTask: skipping malformed task "${task.id}":`, err);
        return null;
    }
}

export const { remapClasses, mergeDatasets, copyProjectImages, exportDataset } = ASYNC_TASK_TYPES;
